import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime, Subscription } from 'rxjs';
import { ConfirmDialogComponent, DEFAULT_SNACKBAR_CONFIG, Logger } from 'src/app/@shared';
import {
  VersionAttributeFilterSelection,
  VersionAttributeSearchResult,
} from '../../models/store-attribute.model';
import {
  VersionAssociationEntityType,
  VersionAssociationFilterService,
} from '../../services/version-association-filter.service';
import { VersionOfferService } from '../../services/version-offer.service';
import { VersionPromoService } from '../../services/version-promo.service';
import { VersionEntityAssociationAttributeSearchComponent } from '../version-entity-association-attribute-search/version-entity-association-attribute-search.component';

const log = new Logger('VersionEntityAssociationDialogComponent');

export interface VersionEntityAssociationDialogData {
  eventId: string;
  entityType: VersionAssociationEntityType;
  entityId: string;
}

@Component({
  selector: 'app-version-entity-association-dialog',
  templateUrl: './version-entity-association-dialog.component.html',
  styleUrls: ['./version-entity-association-dialog.component.scss'],
})
export class VersionEntityAssociationDialogComponent implements OnInit, OnDestroy {
  @ViewChild(VersionEntityAssociationAttributeSearchComponent)
  attributeSearch?: VersionEntityAssociationAttributeSearchComponent;

  filtersForm = new FormGroup({
    search: new FormControl<string>(''),
  });

  activeAttributeFilters: VersionAttributeFilterSelection[] = [];
  selectedVersionNames: string[] = [];
  includePartialMatches = false;

  private apiSearchResults: VersionAttributeSearchResult[] = [];
  private versionCodeSearchSubscription?: Subscription;

  constructor(
    private associationFilterService: VersionAssociationFilterService,
    private versionOfferService: VersionOfferService,
    private versionPromoService: VersionPromoService,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
    public dialogRef: MatDialogRef<VersionEntityAssociationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VersionEntityAssociationDialogData,
  ) {}

  ngOnInit(): void {
    this.associationFilterService.setContext({
      entityType: this.data.entityType,
      entityId: this.data.entityId,
      eventId: this.data.eventId,
    });

    this.setEntityServiceIds();

    this.versionCodeSearchSubscription = this.filtersForm.controls.search.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value) => {
        this.onVersionCodeSearchChange(value ?? '');
      });
  }

  ngOnDestroy(): void {
    this.versionCodeSearchSubscription?.unsubscribe();
    this.associationFilterService.reset();
  }

  get dialogTitle(): string {
    switch (this.data.entityType) {
      case 'promo':
        return 'Add Versions to Promo';
      case 'offerGroup':
        return 'Add Versions to Offer Group';
      default:
        return 'Add Versions to Offer';
    }
  }

  get hasAttributeFilters(): boolean {
    return this.activeAttributeFilters.length > 0;
  }

  get selectedCount(): number {
    return this.selectedVersionNames.length;
  }

  get selectedStoreTotal(): number {
    return this.apiSearchResults
      .filter((result) => this.selectedVersionNames.includes(result.versionName))
      .reduce((sum, result) => sum + result.storeCount, 0);
  }

  get addButtonLabel(): string {
    const count = this.selectedCount;
    const entity = this.entityLabel;
    return count === 1 ? `Add 1 Version to ${entity}` : `Add ${count} Versions to ${entity}`;
  }

  get entityLabel(): string {
    switch (this.data.entityType) {
      case 'promo':
        return 'Promo';
      case 'offerGroup':
        return 'Offer Group';
      default:
        return 'Offer';
    }
  }

  onAttributeFiltersChange(filters: VersionAttributeFilterSelection[]): void {
    this.activeAttributeFilters = filters;
  }

  onSelectedVersionNamesChange(names: string[]): void {
    this.selectedVersionNames = names;
  }

  onSearchResultsChange(results: VersionAttributeSearchResult[]): void {
    this.apiSearchResults = results;
  }

  onIncludePartialMatchesChange(include: boolean): void {
    this.includePartialMatches = include;
  }

  runVersionCodeSearch(): void {
    const search = this.filtersForm.controls.search.value ?? '';
    if (this.attributeSearch?.hasStoreAttributes) {
      this.associationFilterService.setVersionCodeSearch(search);
      this.attributeSearch.applyFilters();
      return;
    }
    this.attributeSearch?.applySimpleSearch(search);
  }

  onVersionCodeSearchChange(value: string): void {
    if (this.attributeSearch?.hasStoreAttributes) {
      if (!this.attributeSearch.hasAppliedFilters) {
        return;
      }
      this.associationFilterService.setVersionCodeSearch(value);
      this.attributeSearch.refreshVersionCodeSearch(value);
      return;
    }
    this.attributeSearch?.applySimpleSearch(value);
  }

  clearVersionCodeSearch(): void {
    this.filtersForm.controls.search.setValue('', { emitEvent: false });
    if (this.attributeSearch?.hasStoreAttributes) {
      if (this.attributeSearch.hasAppliedFilters) {
        this.onVersionCodeSearchChange('');
      }
      return;
    }
    this.attributeSearch?.applySimpleSearch('');
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  addVersions(): void {
    const versionIds = this.resolveSelectedVersionIds();
    if (!versionIds) {
      return;
    }

    if (!versionIds.length) {
      this.matSnackBar.open('Please select at least one version.', 'OK', DEFAULT_SNACKBAR_CONFIG);
      return;
    }

    if (versionIds.length !== this.selectedVersionNames.length) {
      this.matSnackBar.open('Could not resolve all selected versions. Please try searching again.', 'OK', DEFAULT_SNACKBAR_CONFIG);
      return;
    }

    const count = versionIds.length;
    const storeTotal = this.selectedStoreTotal;
    const storeSuffix = storeTotal > 0 ? ` (${storeTotal} stores total)` : '';

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Add Versions',
        message: `Add ${count} version(s) to this ${this.entityLabel.toLowerCase()}${storeSuffix}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.associateVersions(versionIds).subscribe({
        next: () => {
          this.matSnackBar.open('Versions are added', 'OK', DEFAULT_SNACKBAR_CONFIG);
          this.reloadParentList();
          this.dialogRef.close(true);
        },
        error: (error) => {
          log.error('Error adding versions', error);
          const message = error?.error?.value ?? error?.message ?? 'Error adding versions';
          this.matSnackBar.open(message, 'OK', DEFAULT_SNACKBAR_CONFIG);
        },
      });
    });
  }

  private setEntityServiceIds(): void {
    this.versionOfferService.eventId = this.data.eventId;
    this.versionPromoService.eventId = this.data.eventId;

    switch (this.data.entityType) {
      case 'offer':
        this.versionOfferService.offerId = this.data.entityId;
        break;
      case 'promo':
        this.versionPromoService.promoId = this.data.entityId;
        break;
      case 'offerGroup':
        this.versionOfferService.offerGroupId = this.data.entityId;
        break;
    }
  }

  private associateVersions(versionIds: string[]) {
    switch (this.data.entityType) {
      case 'promo':
        return this.versionPromoService.associateVersionToPromo(versionIds);
      case 'offerGroup':
        return this.versionOfferService.associateOfferGVersions(versionIds);
      default:
        return this.versionOfferService.associateOfferToVersion(versionIds);
    }
  }

  private reloadParentList(): void {
    switch (this.data.entityType) {
      case 'promo':
        this.versionPromoService.resetpage();
        this.versionPromoService.reload();
        break;
      case 'offerGroup':
        this.versionOfferService.offerGreload();
        break;
      default:
        this.versionOfferService.resetpage();
        this.versionOfferService.reload();
        break;
    }
  }

  private resolveSelectedVersionIds(): string[] | null {
    const unmatched: string[] = [];
    const ids = this.selectedVersionNames
      .map((name) => {
        const result = this.apiSearchResults.find((r) => r.versionName === name);
        if (!result?.versionId) {
          unmatched.push(name);
        }
        return result?.versionId;
      })
      .filter((id): id is string => !!id);

    if (unmatched.length) {
      this.matSnackBar.open(
        `Could not resolve version(s): ${unmatched.join(', ')}`,
        'OK',
        DEFAULT_SNACKBAR_CONFIG,
      );
      return null;
    }

    return ids;
  }
}
